import { isAppointment } from "@/features/appointments/utils";
import { type OpenModalProps } from "@/features/calendar/openCalendarModal";
import {
  type CoWorkerCalendarOutlook,
  type CoWorkerScheduleActivity,
  type OutlookEvent,
} from "@/features/calendar/types";
import {
  getAppointmentTitle,
  getColor,
  getIsDashed,
  getRenderTypeInfo,
  isScheduleActivity,
  mapToCalendarItem,
} from "@/features/calendar/utils";
import { getTaskTitle } from "@/features/tasks/utils";
import { type Appointment, type Task, isTask } from "@repo/api";
import { useSettings } from "@repo/ui";

import { isOutlookEvent } from "../useUserCalendar";
import { AppointmentCells } from "./AppointmentCells";
import { OutlookCells } from "./OutlookCells";
import { ScheduleCells } from "./ScheduleCells";
import { TaskCells } from "./TaskCells";
import { ColorCell, RowContent, StyledRow } from "./components";

export const AgendaItem = ({
  item,
  openModal,
  mutateSearchAppointments,
}: {
  item: Task | Appointment | OutlookEvent | CoWorkerScheduleActivity;
  openModal?: (props: OpenModalProps) => void;
  mutateSearchAppointments?: () => void;
}) => {
  const { task_configuration, calendar } = useSettings();
  const typeInfo = getRenderTypeInfo(item);

  if (!typeInfo) {
    return null;
  }

  const colorInfo = getColor(
    typeInfo,
    task_configuration?.task_types,
    calendar?.activity_configuration
  );

  const onItemClick = () => {
    if (isTask(item)) {
      openModal?.({ type: "task", id: String(item.id) });
    } else if (isAppointment(item)) {
      openModal?.({
        type: "appointment",
        opportunityId: item.opportunityId,
        appointmentId: item.appointmentId,
        dropInId: item.appointmentId,
        id: item.id,
      });
    } else if (isOutlookEvent(item)) {
      const mappedItem = mapToCalendarItem([item])?.[0];
      openModal?.({
        type: "outlook",
        item: mappedItem as CoWorkerCalendarOutlook,
      });
    } else if (isScheduleActivity(item)) {
      openModal?.({ type: "scheduled_activity", item });
    }
  };

  const isInteractive = Boolean(openModal);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      // Enter activates on keydown, matching native button behaviour.
      e.preventDefault();
      onItemClick();
    } else if (e.key === " ") {
      // Space only prevents scrolling on keydown; it activates on keyup.
      e.preventDefault();
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === " ") {
      e.preventDefault();
      onItemClick();
    }
  };

  const ariaLabel = isTask(item)
    ? getTaskTitle(item)
    : isAppointment(item)
      ? getAppointmentTitle(item)
      : isOutlookEvent(item)
        ? item.subject
        : isScheduleActivity(item)
          ? item.title
          : undefined;

  return (
    <StyledRow
      {...(isInteractive
        ? {
            role: "button",
            tabIndex: 0,
            "aria-label": ariaLabel,
            onClick: onItemClick,
            onKeyDown,
            onKeyUp,
          }
        : {})}
    >
      <ColorCell
        borderColor={colorInfo.borderColor}
        dashed={getIsDashed(typeInfo)}
      />
      <RowContent>
        {isTask(item) && (
          <TaskCells task={item} backgroundColor={colorInfo.color} />
        )}
        {isAppointment(item) && (
          <AppointmentCells
            item={item}
            mutateSearchAppointments={mutateSearchAppointments}
            backgroundColor={colorInfo.color}
          />
        )}
        {isOutlookEvent(item) && (
          <OutlookCells event={item} backgroundColor={colorInfo.color} />
        )}
        {isScheduleActivity(item) && (
          <ScheduleCells item={item} backgroundColor={colorInfo.color} />
        )}
      </RowContent>
    </StyledRow>
  );
};
